import { INoiseInfo } from "../store/useNoiseInfo";
import { RepairIssueMap } from "../store/useRepairIssueInfo";
import { NOISE_SUB_CODE, toNoiseRecurrent } from "../config/constants";
import { toInnerLastIssueTime } from "../utils/lastIssueTime";

interface CollectInfoHandlers {
  /** 回填附件 UUID 列表 */
  setAttachmentIds: (ids: string[]) => void;
  /** 清空异响 store（防跨订单串单） */
  resetNoiseInfo: () => void;
  /** 按后端数据水合异响 store */
  hydrateNoiseInfo: (data: Partial<INoiseInfo>) => void;
  /** 按后端数据水合各维修子项问题描述 store */
  hydrateIssues: (map: RepairIssueMap) => void;
}

/**
 * 判断 collectInfo 是否存在「异响反馈」——异响问题描述可为空（非必填），
 * 故只要位置/能否重现/问题描述/附件任一有值即视为存在异响反馈。
 * 供水合（决定是否写入异响 store）、只读页（是否渲染异响子项）、详情页（是否展示「问题描述」入口）共用，避免多处判定漂移。
 */
export function hasNoiseFeedback(collectInfo: any): boolean {
  const ni = collectInfo?.noiseInfo || {};
  const noiseAttach = Array.isArray(ni.noiseAttachmentList)
    ? ni.noiseAttachmentList
    : [];
  return (
    !!ni.noisePosition ||
    toNoiseRecurrent(ni.noiseRecurrent) != null ||
    !!ni.noiseProblemDesc ||
    noiseAttach.length > 0
  );
}

/**
 * 回显「预约信息采集内容」collectInfo（附件 / 异响 / 各维修子项问题描述+最近一次问题时间）到表单与 store。
 * 详情接口把这些字段统一挂在 collectInfo 下（异响挂 collectInfo.noiseInfo，故障反馈挂 collectInfo.faultFeedbackList）；
 * 无采集内容时 collectInfo 为默认值对象，各字段安全兜底、不误填。
 *
 * 最近一次问题时间（lastIssueTime）随各维修二级项挂在 faultFeedbackList[].lastIssueTime，
 * 配合 unknownPrecision（0-无 / 1-小时未知 / 2-分钟未知）还原为带未知标记的内部值。
 */
export function hydrateCollectInfo(
  collectInfo: any,
  handlers: CollectInfoHandlers
) {
  const collect = collectInfo || {};
  const { setAttachmentIds, resetNoiseInfo, hydrateNoiseInfo, hydrateIssues } =
    handlers;

  // 附件（后端返回 fileId 列表；无则空）
  setAttachmentIds(collect.attachmentList || []);

  // 各维修子项问题描述汇总：faultFeedbackList（非异响） + noiseInfo（异响子项 code=103）
  const issueMap: RepairIssueMap = {};

  const list: any[] = Array.isArray(collect.faultFeedbackList)
    ? collect.faultFeedbackList
    : [];
  list.forEach((it) => {
    if (it?.itemCode == null) return;
    issueMap[String(it.itemCode)] = {
      desc: it.problemDesc || "",
      attachmentIds: Array.isArray(it.attachmentList) ? it.attachmentList : [],
      lastIssueTime: toInnerLastIssueTime(it.lastIssueTime, it.unknownPrecision),
    };
  });

  // 异响字段统一读 collectInfo.noiseInfo（NoiseInfoRes）
  const ni = collect.noiseInfo || {};
  const noiseRecurrent = ni.noiseRecurrent;
  const noisePositionImg = ni.noisePositionImg;
  const noisePosition = ni.noisePosition;
  // 异响问题描述
  const noiseProblemDesc = ni.noiseProblemDesc;
  const noiseAttach = Array.isArray(ni.noiseAttachmentList)
    ? ni.noiseAttachmentList
    : [];

  // 先清空全局 store（防同一会话上一单残留串到本单），再按坐标/字段水合
  resetNoiseInfo();
  const hasNoise = hasNoiseFeedback(collect);
  if (hasNoise) {
    // 坐标形如 "0.1234,0.5678"；须先判空再 split，避免空串被 Number("") 误解析为 0（有效坐标）
    const posStr = String(noisePosition || "").trim();
    const [nx, ny] = posStr
      ? posStr.split(",").map(Number)
      : [NaN, NaN];
    hydrateNoiseInfo({
      xPct: !Number.isNaN(nx) ? nx : undefined,
      yPct: !Number.isNaN(ny) ? ny : undefined,
      positionImg: noisePositionImg || "",
      // noiseRecurrent 归一化：后端有效值仅 0/1（兼容数字字符串），其余（-1 / null / 非法值）→ null，
      // 与 hasNoiseFeedback 及必填校验（noiseRecurrent != null）保持一致
      noiseRecurrent: toNoiseRecurrent(noiseRecurrent),
    });
    // 异响子项的问题描述文本 + 附件写入维修子项 store（页面按 code=103 读取；异响无最近一次问题时间）
    issueMap[String(NOISE_SUB_CODE)] = {
      desc: noiseProblemDesc || "",
      attachmentIds: noiseAttach,
      lastIssueTime: "",
    };
  }

  hydrateIssues(issueMap);
}
